import { Component, inject, signal } from '@angular/core';
import {
    DocumentController,
    DocumentTypeEnum,
    FeatureFlagController,
    OrderEntity,
} from '@domain';
import { TranslatePipe } from '@ngx-translate/core';
import { RaIconComponent } from 'ra-ui';
import { take } from 'rxjs';
import { DocumentCreateController } from 'src/app/domain/controllers/document/document-create.controller';
import { OrderCreateComponent } from 'src/app/modules/order/components/create/order-create.component';
import { ImportDocumentsModalComponent } from 'src/app/modules/order/components/import-documents-modal/import-documents-modal.component';
import { RaButtonComponent } from '@shared/components';
import { OrderArchivedPanelComponent } from '../order-archived-panel/order-archived-panel.component';
import { OrderTrashPanelComponent } from '../order-trash-panel/order-trash-panel.component';
import { FeedMainService } from '../../services/feed-main.service';
import { OrderMainService } from '../../services/order-main.service';

/**
 * Tela de boas-vindas do primeiro uso (feed unificado): substitui os dois
 * painéis vazios ("Nada por aqui" / "Nenhum serviço encontrado") por um canvas
 * único com as duas ações de criação — serviço ou documento avulso — e um
 * mini-guia do fluxo. Some sozinha assim que existir qualquer item no feed
 * (a page decide via `showWelcome`).
 *
 * As ações espelham o menu "+ Novo" do feed-list: serviço abre o order-create;
 * documento avulso abre o passo com chat/importar/manual. Arquivados e lixeira
 * continuam acessíveis por botões no rodapé (uma conta pode ficar
 * "vazia" só porque arquivou tudo).
 */
@Component({
    selector: 'order-welcome',
    templateUrl: 'order-welcome.component.html',
    styleUrl: 'order-welcome.component.scss',
    imports: [
        RaButtonComponent,
        RaIconComponent,
        OrderCreateComponent,
        ImportDocumentsModalComponent,
        OrderArchivedPanelComponent,
        OrderTrashPanelComponent,
        TranslatePipe,
    ],
})
export class OrderWelcomeComponent {
    /** Passo do canvas: escolha raiz (serviço/documento), opções do documento
     *  ou o tipo do documento avulso. */
    public level = signal<'root' | 'document' | 'document-type'>('root');

    public documentTypeEnum = DocumentTypeEnum;

    public isModalCreateOrderVisible = signal<boolean>(false);
    public isImportDocumentsVisible = signal<boolean>(false);
    public isArchivedPanelVisible = signal<boolean>(false);
    public isTrashPanelVisible = signal<boolean>(false);

    // * [Injects]
    public feedMainService = inject(FeedMainService);
    public orderMainService = inject(OrderMainService);
    public featureFlagController = inject(FeatureFlagController);
    private _documentController = inject(DocumentController);
    private _documentCreateController = inject(DocumentCreateController);

    /** Card "Criar um serviço" → modal de criação (mesmo fluxo do feed-list). */
    public newService(): void {
        this.isModalCreateOrderVisible.set(true);
    }

    /** Serviço criado: vira a order atual e o reload do feed dispensa o welcome. */
    public orderCreated(order: OrderEntity): void {
        this.isModalCreateOrderVisible.set(false);
        this.orderMainService.setOrder(order);
        this.feedMainService.getFeed().subscribe();
    }

    /** "Usar o chat" → bolinha de IA em modo avulso (a page observa o tick). */
    public newDocumentByChat(): void {
        this.feedMainService.requestChatStandalone();
    }

    /** "Importar arquivos" → modal de import em modo AVULSO (orderId null). */
    public newDocumentByImport(): void {
        this.isImportDocumentsVisible.set(true);
    }

    /** Import concluído: reload traz os documentos em conversão (PENDING). */
    public onImportCreated(): void {
        this.feedMainService.getFeed().subscribe();
    }

    /** Tipo escolhido → cria o avulso desse tipo e navega para o editor. */
    public newStandaloneDocument(type: DocumentTypeEnum): void {
        this._documentCreateController
            .insertStandalone(type)
            .pipe(take(1))
            .subscribe(document => {
                if (!document?._id) return;
                this._documentController.navigateToDocument(document, true);
            });
    }

    /** Fechou o painel de arquivados: um reopen pode ter repovoado o feed. */
    public onArchivedClosed(): void {
        this.isArchivedPanelVisible.set(false);
        this.feedMainService.getFeed().subscribe();
    }

    /** Fechou a lixeira: um restore pode ter repovoado o feed. */
    public onTrashClosed(): void {
        this.isTrashPanelVisible.set(false);
        this.feedMainService.getFeed().subscribe();
    }
}
